/**
 * @data-dashboard/shared — THE NEEDS REGISTRY (founder 2026-07-13).
 *
 * ONE canonical list of the things we need from an account before a number can
 * be shown honestly. Before this, "you haven't set X up yet" was expressed four
 * different ways — a locked card with a sample number, the CM-gap warning, the
 * profit-model's empty lever slots, and the setup checklist — that could all
 * disagree. This registry is the single source of truth they now share:
 *
 *   - the SERVER computes each need's met/unmet state in ONE place
 *     (apps/api/src/services/metrics/needs.ts) from existing data, and
 *   - the DASHBOARD renders it in ONE style — a warning on every affected card
 *     (never a pretend number) and a banner at the top of the page — and
 *   - the SETUP CHECKLIST reads the same derivation, so they can never diverge.
 *
 * ADDING A NEED LATER is one entry here (id + copy + fix route) plus one line in
 * the server derivation. E.g. an "accounting_connection" need (QuickBooks) drops
 * in the same way overhead does today.
 *
 * COPY RULE (CLAUDE.md): the founder is a marketer, not an engineer. Every
 * string here is what he'd say to a client — no jargon, complete sentences.
 */

/** The canonical set. Keep in sync with the server derivation + the web
 *  Capability→need map (apps/web/src/lib/card-requirements.ts). */
export type NeedId =
  | "store_connection" // the platform that holds the orders isn't connected
  | "product_costs" // no per-product costs (contribution margin can't be computed)
  | "product_categorization" // catalog isn't organised / products await a quick review
  | "marketing_spend" // no ad spend AND no affiliate-commission source in the window
  | "overhead"; // no monthly overhead entered (and no accounting connection yet)

export interface NeedDef {
  id: NeedId;
  /** Founder-facing short name, lower-case so it slots into a sentence
   *  ("We're missing your overhead"). */
  label: string;
  /** One plain line: what having this unlocks / why we ask. Registry reference
   *  + the source for any "why is this here" tooltip. */
  unlocks: string;
  /** The banner headline when this need is unmet ("We're missing your overhead").
   *  A full clause on its own — the banner appends " — fix it →". */
  bannerLead: string;
  /** The card-level warning sentence shown in place of a number on any card that
   *  needs this. Plain language, never red-alarm ("nothing is broken"). */
  cardWarning: string;
  /** Where the "fix it" link goes (the costs chat / connections / categorize
   *  review). A component may add router state to PRE-SCOPE it (e.g. the costs
   *  chat opened with the exact missing products) — this is the base route. */
  fixTo: string;
  /** The fix link's label. */
  fixCta: string;
}

/**
 * The registry. Order here is the order needs stack in a banner (most
 * foundational first: you connect the store, then cost + organise products,
 * then add spend + overhead).
 */
export const NEEDS: Record<NeedId, NeedDef> = {
  store_connection: {
    id: "store_connection",
    label: "your store connection",
    unlocks: "Connecting the platform that holds your orders is what fills the whole dashboard with your real numbers.",
    bannerLead: "We're not connected to the store that holds your orders",
    cardWarning: "Connect the platform that holds your orders to see this.",
    fixTo: "/connections",
    fixCta: "Connect your store",
  },
  product_costs: {
    id: "product_costs",
    label: "your product costs",
    unlocks: "Your per-product costs turn revenue into true profit — margin, CM-ROAS, LTV and the profit levers all come from them.",
    bannerLead: "We're missing your product costs, so profit can't be calculated yet",
    cardWarning: "We can't calculate your true profit yet — your product costs aren't set up.",
    fixTo: "/costs",
    fixCta: "Set up costs",
  },
  product_categorization: {
    id: "product_categorization",
    label: "your product organization",
    unlocks: "Grouping your SKUs into products (and reviewing new ones as they start selling) keeps every product's profit tied to the right thing.",
    bannerLead: "A few products still need a quick review before their profit is right",
    cardWarning: "A few products still need a quick review before this is right.",
    fixTo: "/onboarding/chat?stage=categorize&review=1",
    fixCta: "Review products",
  },
  marketing_spend: {
    id: "marketing_spend",
    label: "your marketing spend",
    unlocks: "Your ad spend and affiliate commissions are what let us judge whether marketing is paying off — MER, CAC and LTV:CAC.",
    bannerLead: "We're missing your marketing spend",
    cardWarning: "We can't calculate this yet — we don't know your marketing spend.",
    fixTo: "/welcome",
    fixCta: "Add marketing spend",
  },
  overhead: {
    id: "overhead",
    label: "your overhead",
    unlocks: "Your monthly overhead is the last piece of true break-even, operating profit and how efficiently the business scales.",
    bannerLead: "We're missing your overhead",
    cardWarning: "We can't calculate this yet — add your overhead.",
    fixTo: "/welcome",
    fixCta: "Add overhead",
  },
};

/** Registry as an ordered list (banner + iteration order). */
export const NEED_LIST: readonly NeedDef[] = Object.values(NEEDS);

/** The partial-costing detail (mirrors the CM-gap shape) carried on the
 *  product_costs need when SOME products are costed and some aren't — so a card
 *  can name the exact missing products. Structurally identical to the api/web
 *  CmGap type; duplicated here to keep /shared free of app imports. */
export interface NeedCmGap {
  missingProducts: number;
  topNames: string[];
}

/**
 * A need's live state for one account — what the server sends the dashboard.
 * `met` is the whole story for most needs; the optional detail fields let a card
 * or banner say something specific (which products, how many to review).
 */
export interface NeedStatus {
  id: NeedId;
  met: boolean;
  /** product_costs only: the partial-costing gap (some products uncosted). null
   *  when fully costed OR when there are no costs at all (the plain unmet state). */
  cmGap?: NeedCmGap | null;
  /** product_categorization only: how many selling SKUs still await a review. */
  reviewCount?: number;
}

/** The unmet needs, in registry order. The one helper both the banner and any
 *  "what's left" surface should use, so they agree on what counts as unmet. */
export function unmetNeeds(statuses: readonly NeedStatus[]): NeedStatus[] {
  const byId = new Map(statuses.map((s) => [s.id, s]));
  return NEED_LIST.map((d) => byId.get(d.id)).filter(
    (s): s is NeedStatus => s !== undefined && !s.met,
  );
}

// ---------------------------------------------------------------------------
// Page banner — which needs each dashboard page's cards depend on
// ---------------------------------------------------------------------------

/**
 * Per dashboard page (keyed by URL segment = the HideProvider pageKey), one
 * entry per need-dependent card listing the need(s) that card depends on. The
 * page banner reads this to say "We're missing your overhead — affects N cards
 * on this page", where N = the cards here that depend on that need.
 *
 * "Affects" = depends on (the need is in the card's requirement set) — the
 * useful "fixing this unblocks these cards" framing, matching MetricCard's own
 * `requires`. A card that depends on two needs is counted under both.
 *
 * This is a curated map of the CURRENT need-gated cards — the frontend
 * equivalent of the card catalog's placements. Kept here (with the registry, in
 * /shared) so the count logic has a home the API test suite can cover — web has
 * no test runner. Update it alongside a page's cards; pages with no
 * need-dependent cards (Products, Retention today) are simply absent → no banner.
 * A slightly stale count only nudges; it never blocks or shows a wrong number.
 */
export const PAGE_CARD_NEEDS: Record<string, readonly (readonly NeedId[])[]> = {
  // Exec — the KPI ratio cards + the 5-lever profit-model cockpit + PIP hero.
  dashboard: [
    ["marketing_spend"], // MER
    ["marketing_spend"], // Blended CAC
    ["overhead", "product_costs"], // Operational drag
    ["product_costs"], // Profit model · Contribution margin
    ["product_costs", "marketing_spend"], // Profit model · Acquisition economics
    ["product_costs", "marketing_spend"], // Profit model · LTV : CAC
    ["overhead", "product_costs"], // Profit model · Operational efficiency
    ["product_costs", "marketing_spend", "overhead"], // Profit model · Capital return velocity
    ["overhead", "product_costs"], // Profit model · Profit inflection point
  ],
  marketing: [
    ["marketing_spend"], // MER (blended)
    ["marketing_spend"], // aMER (blended)
    ["marketing_spend"], // Marketing spend
    ["marketing_spend"], // Blended CAC
    ["marketing_spend"], // LTV : CAC
    ["product_costs", "marketing_spend"], // CM-MER
    ["product_costs", "marketing_spend"], // CM-aMER
    ["product_costs", "marketing_spend"], // CM-LTV : CAC
    ["product_costs"], // Day-1 CM-ROAS by channel
  ],
  operations: [
    ["overhead", "product_costs"], // Operational drag
    ["overhead", "product_costs"], // Operating profit / efficiency waterfall
    ["overhead"], // Overhead per customer
  ],
  financials: [
    ["product_costs"], // Contribution-margin lever
    ["product_costs", "marketing_spend", "overhead"], // Capital return velocity
    ["overhead", "product_costs"], // Profit inflection point
  ],
  cohorts: [
    ["product_costs"], // Profit (CM) customer-value view
  ],
  subscriptions: [
    ["product_costs"], // Profit (CM) subscription view
  ],
};

/** How many of a page's cards depend on a given need (the banner's count). */
export function affectedCardCount(
  pageCardNeeds: readonly (readonly NeedId[])[] | undefined,
  needId: NeedId,
): number {
  if (!pageCardNeeds) return 0;
  return pageCardNeeds.filter((cardNeeds) => cardNeeds.includes(needId)).length;
}

/** One banner line per unmet need that actually affects this page's cards, in
 *  registry order, each with its affected-card count. Needs with no affected
 *  card on the page are dropped (nothing to nag about there). Pure — the banner
 *  renders straight from this. */
export function bannerNeedLines(
  pageKey: string,
  unmetNeedIds: readonly NeedId[],
): { needId: NeedId; count: number }[] {
  const pageCardNeeds = PAGE_CARD_NEEDS[pageKey];
  if (!pageCardNeeds) return [];
  const unmet = new Set(unmetNeedIds);
  return NEED_LIST.filter((d) => unmet.has(d.id))
    .map((d) => ({ needId: d.id, count: affectedCardCount(pageCardNeeds, d.id) }))
    .filter((line) => line.count > 0);
}
